import { useWorkspace2Store } from '../../../Workspace2Store'
import { DataNode } from '../../../types/types'
import { findNodeByContentItemId } from '../utils'

// Constants for polling
export const STORE_UPDATE_POLL_INTERVAL_MS = 50
export const STORE_UPDATE_MAX_ATTEMPTS = 60 // 60 * 50ms = 3 seconds max (increased for BP sync)

interface WaitForStoreUpdateResult {
    storeUpdated: boolean
}

interface BuildMutationInputFromFreshStateParams {
    contentId: string
}

interface BuildMutationInputFromFreshStateResult {
    freshNode: DataNode | null
    nodeWithFreshElements: DataNode | null
}

/**
 * Polls the store for updates to a specific content item.
 * Compares element signatures (IDs) before and after to detect changes.
 *
 * @param contentId - The content item ID to watch for updates
 * @param oldSignature - The element signature before the update (comma-separated element IDs)
 * @returns Promise that resolves when store updates or times out
 */
export const waitForStoreUpdate = async (
    contentId: string,
    oldSignature: string
): Promise<WaitForStoreUpdateResult> => {
    let storeUpdated = false

    for (let i = 0; i < STORE_UPDATE_MAX_ATTEMPTS; i++) {
        await new Promise((resolve) =>
            setTimeout(resolve, STORE_UPDATE_POLL_INTERVAL_MS)
        )

        const freshContentItem = useWorkspace2Store
            .getState()
            .contentItemViewNodes.find((item) => item.id === contentId)

        const newSignature =
            freshContentItem?.elements?.map((el) => el.id).join(',') || ''

        // Check if signature changed (store updated)
        if (newSignature !== oldSignature) {
            storeUpdated = true
            break
        }

        // If we've reached max attempts without detecting change, break without setting storeUpdated
        if (i >= STORE_UPDATE_MAX_ATTEMPTS - 1) {
            break
        }
    }

    return { storeUpdated }
}

/**
 * Builds mutation input from fresh store state after a sync operation.
 * Reads fresh tree data and content item nodes to avoid stale closures.
 *
 * @param params - Parameters including content ID and node finder function
 * @returns Object containing fresh node and node with fresh elements, or null if not found
 */
export const buildMutationInputFromFreshState = ({
    contentId,
}: BuildMutationInputFromFreshStateParams): BuildMutationInputFromFreshStateResult => {
    // Read fresh state from store (no stale closures)
    const freshState = useWorkspace2Store.getState()
    const freshTreeData = freshState.treeData
    const freshContentItemViewNodes = freshState.contentItemViewNodes

    // Find the clicked node in fresh tree data
    const freshNode = findNodeByContentItemId(contentId, freshTreeData)

    if (!freshNode) {
        return {
            freshNode: null,
            nodeWithFreshElements: null,
        }
    }

    // Find fresh content item node
    const freshContentItemNode = freshContentItemViewNodes.find(
        (item) => item.id === contentId
    )

    // Create node with fresh elements
    const nodeWithFreshElements = freshContentItemNode
        ? {
              ...freshNode,
              contentItemView: {
                  ...freshNode.contentItemView,
                  elements: freshContentItemNode.elements || [],
              },
          }
        : freshNode

    return {
        freshNode,
        nodeWithFreshElements,
    }
}
